import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {NodeApiService} from '../network/online/node/node-api.service';
import {GroupsApiService} from '../users/groups/groups-api.service';
import {VoipPeerApiService} from '../network/online/voip-peer/voip-peer-api.service';
import {PrefixGroupsApiService} from '../settings/call-flow-configurations/prefix-groups/prefix-groups-api.service';
import {AttributeManipulationGroupsApiService} from '../settings/call-flow-configurations/attribute-manipulation-groups/attribute-manipulation-groups-api.service';
import {PeerConnectionApiService} from '../peer-connection/peer-connection-api.service';
import {CustomersApiService} from '../customers/customers-api.service';
import {TimeBasedRoutingApiService} from '../settings/routing/time-based-routing/time-based-routing-api.service';
import {OfflineNodeApiService} from '../network/offline/node/offline-node-api.service';
import {OfflinePeerConnectionApiService} from '../network/offline/peer-connection/offline-peer-connection-api.service';
import {OfflineVoipPeerApiService} from '../network/offline/voip-peer/offline-voip-peer-api.service';
import {ResourceGroupApiService} from '../../../network/services/online/resource-group/resource-group-api.service';
import {UrlQueryService} from '../../common/url-query/url-query.service';
import * as _ from 'lodash';
import {SipConditionGroupApiService} from '../settings/call-flow-configurations/sip-condition-group/sip-condition-group-api.service';
import {SipManipulationGroupApiService} from '../settings/call-flow-configurations/sip-manipulation-groups/sip-manipulation-group-api.service';

@Injectable({
  providedIn: 'root'
})
export class RoutingGroupApiService {

  constructor(private http: HttpClient,
              private nodeApiService: NodeApiService,
              private peerConnectionApiService: PeerConnectionApiService,
              private userGroupsApiService: GroupsApiService,
              private voipPeerApiService: VoipPeerApiService,
              private prefixGroupsApiService: PrefixGroupsApiService,
              private attributeManipulationGroupsApiService: AttributeManipulationGroupsApiService,
              private timeBasedRoutingApiService: TimeBasedRoutingApiService,
              private offlineNodeApiService: OfflineNodeApiService,
              private offlinePeerConnectionApiService: OfflinePeerConnectionApiService,
              private offlineVoipPeerApiService: OfflineVoipPeerApiService,
              private resourceGroupsApiService: ResourceGroupApiService,
              private customersApiService: CustomersApiService,
              private sipConditionGroupApiService: SipConditionGroupApiService,
              private sipManipulationGroupApiService: SipManipulationGroupApiService) {
  }

  private readonly routingGroupUrl: string = 'routing/routingGroup';

  onlineNodeImg = 'assets/images/topology/node_online.svg';
  offlineNodeImg = 'assets/images/topology/node_offline.svg';
  nodeGroupImg = 'assets/images/topology/node_group.svg';

  onlinePeerConnectionImg = 'assets/images/topology/peer_connection_online.svg';
  offlinePeerConnectionImg = 'assets/images/topology/peer_connection_offline.svg';
  peerConnectionGroupImg = 'assets/images/topology/peer_connection_group.svg';

  onlineVoipPeerImg = 'assets/images/topology/voip_peer_online.svg';
  offlineVoipPeerImg = 'assets/images/topology/voip_peer_offline.svg';
  voipPeerGroupImg = 'assets/images/topology/voip_peer_group.svg';

  public getGroups(params: any = {}): Promise<any> {
    const generatedRoutingGroupUrl = (!params || _.isEmpty(params)) ?
      `${this.routingGroupUrl}?filter=(slim=true)` : this.addQueryParameters(this.routingGroupUrl, params);

    return this.http.get(generatedRoutingGroupUrl).toPromise();
  }

  public addGroup(group): Promise<any> {
    return this.http.post(this.routingGroupUrl, group).toPromise();
  }

  public editGroup(group): Promise<any> {
    return this.http.put(`${this.routingGroupUrl}/${group.id}`, group).toPromise();
  }

  public getGroupsSlim(): Promise<any> {
    return this.http.get(`${this.routingGroupUrl}?filter=(slim=true)`).toPromise();
  }

  public deleteGroup(groupId): Promise<any> {
    return this.http.delete(`${this.routingGroupUrl}/${groupId}`).toPromise();
  }

  public moveExistingGroup(groupId, group): Promise<any> {
    return this.http.put(`${this.routingGroupUrl}/${groupId}`, group).toPromise();
  }

  public getRulesForGroup(params): Promise<any> {
    const groupId = params.groupId;
    delete params.groupId;

    return this.http.get(this.addQueryParameters(`${this.routingGroupUrl}/${groupId}`, params)).toPromise();
  }

  public getGroupById(id): Promise<any> {
    return this.http.get(`${this.routingGroupUrl}/${id}`).toPromise();
  }

  public externalData(): Promise<any> {
    return Promise.all([
      this.nodeApiService.getNodes(),
      this.peerConnectionApiService.getPCons(),
      this.userGroupsApiService.getGroups(),
      this.voipPeerApiService.getVoipPeers(),
      this.prefixGroupsApiService.getPrefixGroupsSlim().toPromise(),
      this.attributeManipulationGroupsApiService.getAttributeManipulationGroups().toPromise(),
      this.timeBasedRoutingApiService.getTimeConditions().toPromise(),
      this.offlineNodeApiService.getNodes({isOffline: {operator: '=', value: 'true'}}),
      this.offlinePeerConnectionApiService.getPcons({isOffline: {operator: '=', value: 'true'}}),
      this.offlineVoipPeerApiService.getVoipPeers({isOffline: {operator: '=', value: 'true'}}),
      this.resourceGroupsApiService.getGroups(),
      this.customersApiService.getCustomers().toPromise(),
      this.sipConditionGroupApiService.getSipConditionGroups().toPromise(),
      this.sipManipulationGroupApiService.getSipManipulationGroups().toPromise()
    ]).then(data => {
      return {
        nodes: this.getRelevantData(data[0], 'Online Node', this.onlineNodeImg),
        pCons: data[1].elements.map(this.generatePeerConnection),
        userGroups: this.getRelevantData(data[2]),
        voipPeers: this.getRelevantData(data[3].elements, 'Online VoIP Peer', this.onlineVoipPeerImg),
        prefixGroups: this.getRelevantData(data[4].elements),
        attributeGroups: this.constructAttributeGroups(data[5]),
        timeConditions: this.getRelevantData(data[6]),
        offlineNodes: this.getRelevantData(data[7], 'Offline Node', this.offlineNodeImg),
        offlinePCons: data[8].map(this.generateOfflinePeerConnection),
        offlineVoipPeers: this.getRelevantData(data[9], 'Offline VoIP Peer', this.offlineVoipPeerImg),
        resourceGroups: data[10].elements.map(this.generateResourceGroup),
        customers: data[11].elements,
        sipConditionGroups: data[12],
        sipManipulationGroups: data[13]
      };
    });
  }

  public getExternalDataForAddOrEditGroup(): Promise<any> {
    return Promise.all([
      this.getGroupsSlim(),
      this.timeBasedRoutingApiService.getTimeConditions().toPromise()
    ]);
  }


  private getDisplayType(type): string {
    switch (type) {
      case 'NODE':
        return 'Node';
      case 'PEER_CONNECTION':
        return 'Peer Connection';
      case 'VOIP_PEER':
        return 'VoIP Peer';
    }
  }

  private getGroupImage(type): string {
    switch (type) {
      case 'NODE':
        return this.nodeGroupImg;
      case 'PEER_CONNECTION':
        return this.peerConnectionGroupImg;
      case 'VOIP_PEER':
        return this.voipPeerGroupImg;
    }
  }

  private generateResourceGroup = (element) => {
    return {
      id: element.id,
      name: element.name,
      elementType: `${this.getDisplayType(element.type)}s group`,
      displayType: this.getDisplayType(element.type),
      image: this.getGroupImage(element.type),
      isTopologyGroup: true,
      numberOfElements: element.elements ? element.elements.length : 1
    };
  }

  private generateOfflinePeerConnection = (element) => {
    return {
      id: element.id,
      name: element.name,
      nodeName: element.nodeName,
      nodeId: element.nodeId,
      elementType: 'Offline Peer Connection',
      image: this.offlinePeerConnectionImg,
      peerConnectionNodeType: element.peerConnectionNodeType
    };
  }

  private generatePeerConnection = (element) => {
    return {
      id: element.id,
      name: element.name,
      nodeName: element.nodeName,
      nodeId: element.nodeId,
      elementType: 'Online Peer Connection',
      image: this.onlinePeerConnectionImg,
      pConNodeType: element.pConNodeType,
      peerConnectionNodeType: element.peerConnectionNodeType
    };
  }

  private getRelevantData(elements, type = null, image = null): any {
    if (type) {
      return elements.map(element => {
        return {id: element.id, name: element.name, elementType: type, image};
      });
    }

    return elements.map(element => {
      return {id: element.id, name: element.name, image};
    });
  }

  private constructAttributeGroups(elements): any {
    return elements.map(element => {
      return {id: element.id, groupName: element.groupName};
    });
  }

  private addQueryParameters(basicUrl, params): string {
    let filter = {};

    if (params && params.filter) {
      if (_.isString(params.filter)) {
        filter = {
          searchString: {
            operator: ':',
            value: params.filter
          }
        };
      } else if (!_.isEmpty(params.filter)) {
        filter = {
          searchString: params.filter.searchString,
          adminState: params.filter.adminState,
          testMode: params.filter.testMode,
          quality: params.filter.quality,
          isDiscard: params.filter.isDiscard,
          isLoopback: params.filter.isLoopback,
          isContinueWithNodeInternalTables: params.filter.isContinueWithNodeInternalTables,
          isRegisteredUsers: params.filter.isRegisteredUsers,
          requestType: params.filter.requestType,
          rrGroupName: params.filter.rrGroupName,
          rrName: params.filter.rrName
        };
      }
    }

    return !_.isEmpty(filter) ? UrlQueryService.addQueryParameters(basicUrl, {filter}) : basicUrl;
  }
}
